import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";
import { debounce } from "../../utils/debounce";
import { getViewportWidth } from "../../utils/getViewportWidth";

@Component({
    tag: "ux-tooltip",
    styleUrl: "tooltip.scss",
    shadow: true
})
export class Tooltip {
    /** the target of the tooltip, hover (or click) on the element activates the tooltip */
    private tooltipTargetElement: HTMLElement | null;
    /**
     * indicates when the tooltip was last activated, used to provide a more accurate
     * timeout for deactivation.
     */
    private lastActivated: number;
    /** the tip of the tooltip is positioned on the corner of the tooltip target */
    private positionTipOnCorner = false;

    /** the tooltip message itself */
    private tooltipElement: HTMLDivElement | undefined;
    /**
     * used to calculate whether the tooltip can be placed in a certain alignment or not,
     * added to the dimensions of the tooltip message to give some extra space around it
     * this is overwritten by the css variable --ux-tooltip-safe-margin
     */
    private safeMargin = 0;
    private tooltipDimensions: { width: number; height: number };

    /** ux-tooltip element */
    @Element() element!: HTMLUxTooltipElement;

    /**
     * The CSS selector of the tooltip target. Tooltip and target
     * must have the same parent element.
     */
    @Prop() readonly tooltipTarget: string = "";

    /** The desired alignment of the tooltip message */
    @Prop() readonly align: "right" | "bottom" | "left" | "top" = "right";

    /** Set to true if the tooltip should be controlled from outside instead of handling UI events itself */
    @Prop() readonly controlled: boolean = false;

    /** the actual alignment of the tooltip message */
    @State() tooltipAlignment: "right" | "left" | "top" | "bottom" | "top-edge" | "bottom-edge" | undefined;
    /** horizontal alignment of the tooltip-tip - this is only used for positionTipOnCorner=true  */
    @State() tooltipTipAlignment: "right" | "left" | undefined;
    /** DOMRects of the tooltip message */
    @State() tooltipPosition: TooltipPosition = {
        top: 0,
        left: 0
    };
    /** activated if there is only text as content of the tooltip */
    @State() textOnlyMode: boolean;
    /** unique ID used to keep track of what tooltip is active */
    @State() uniqueId: string;
    @State() isTooltipActive = false;
    /**
     * if alignment is set to bottom, calculates the max height of the tooltip
     * in relation to the window height
     */
    @State() calculateTooltipMaxHeight = false;
    /**
     * flag to calculate the arrow position after the tooltip position
     * has been calculated
     */
    @State() calculateArrowPosition = false;
    /** DOMRects of the tooltip target */
    private tooltipTargetRects: DOMRect;

    @Watch("controlled")
    setControlled() {
        if (!this.controlled) {
            this.addEventListeners();
        } else {
            this.removeEventListeners();
        }
    }

    @Watch("isTooltipActive")
    calculatePosition() {
        if (this.isTooltipActive) {
            this.calculateTooltipPosition();
            window.addEventListener("scroll", this.scrollHandler);
        } else {
            window.removeEventListener("scroll", this.scrollHandler);
            this.tooltipAlignment = undefined;
        }
    }

    /** Show/activate the tooltip */
    @Method()
    async showTooltip(): Promise<void> {
        if (!this.isTooltipActive) {
            this.activateTooltip();
        }
    }

    /** Hide/deactivate the tooltip */
    @Method()
    async hideTooltip(): Promise<void> {
        this.isTooltipActive = false;
    }

    connectedCallback() {
        this.uniqueId = `ux-tooltip-${idCounter++}`;
    }

    componentWillLoad() {
        this.textOnlyMode = !this.element.firstElementChild;
    }

    private findTargetElement(): HTMLElement | null {
        let target: HTMLElement | null = null;
        let current: HTMLElement | ParentNode | null = this.element.parentElement || this.element.parentNode; // DOM | ShadowDOM
        while (target === null && current !== null) {
            target = current.querySelector(this.tooltipTarget);
            current = current.parentElement;
        }
        return target;
    }

    componentDidLoad() {
        let timeout = 0;
        if (document.querySelector(this.tooltipTarget) === null) {
            timeout = 500;
        }
        // this timeout ensures the DOM is ready
        setTimeout(() => {
            this.tooltipTargetElement = this.findTargetElement();
            if (this.tooltipTargetElement === null) {
                return;
            }
            if (!this.controlled) {
                this.tooltipTargetElement.addEventListener("mouseenter", this.activateTooltip);
                this.tooltipElement?.addEventListener("mouseenter", this.activateTooltip);
                this.tooltipTargetElement.addEventListener("mouseleave", this.deactivateTooltip);
                this.tooltipElement?.addEventListener("mouseleave", this.deactivateTooltip);
                this.tooltipElement?.addEventListener("click", this.handleTooltipClick);
            }
        }, timeout);
        this.positionTipOnCorner =
            window.getComputedStyle(this.element).getPropertyValue("--ux-tooltip-tip-positioning") === "corner";
        this.safeMargin = parseInt(window.getComputedStyle(this.element).getPropertyValue("--ux-tooltip-safe-margin"), 10);
    }

    @State() isPositioned: boolean;

    private scrollHandler = () => {
        this.calculateTooltipPosition();
    };

    private handleTooltipClick = (e: Event) => {
        if ((e.target as HTMLElement).hasAttribute("data-ux-tooltip-close")) {
            this.isTooltipActive = false;
        }
    };

    private activateTooltip = () => {
        if (!this.isTooltipActive) {
            this.activatedTooltipEvent.emit(this.uniqueId);
        }
        this.isTooltipActive = true;
        this.lastActivated = new Date().getTime();
    };

    /**
     * Mainly for mobile: click on the target will open the tooltip,
     * click outside of the tooltip itself will close it.
     */
    @Listen("click", { target: "window" })
    handleClicks(e: Event) {
        if (this.controlled) {
            return;
        }
        if (!this.tooltipTargetElement) {
            return;
        }
        if (this.tooltipTargetElement.contains(e.target as HTMLElement)) {
            if (!this.isTooltipActive) {
                this.isTooltipActive = true;
            }
        } else if ((e.target as HTMLElement).closest("div.box") === null) {
            this.isTooltipActive = false;
        }
    }

    private calculateTooltipPosition = debounce(() => {
        if (!this.tooltipTargetElement || !this.tooltipElement) {
            return;
        }
        this.tooltipDimensions = this.tooltipElement.getBoundingClientRect();
        this.tooltipTargetRects = this.tooltipTargetElement.getBoundingClientRect();
        const invalidTop = this.tooltipTargetRects.top < this.tooltipDimensions.height + this.safeMargin;
        const invalidBottom = window.innerHeight - this.tooltipTargetRects.bottom < this.tooltipDimensions.height + this.safeMargin;
        const verticalMiddle = this.tooltipTargetRects.top + 0.5 * this.tooltipTargetRects.height;
        const horizontalMiddle = this.tooltipTargetRects.left + 0.5 * this.tooltipTargetRects.width;
        const invalidSide =
            this.tooltipTargetRects.bottom - this.tooltipTargetRects.height / 2 < this.tooltipDimensions.height / 2 ||
            window.innerHeight - this.tooltipTargetRects.bottom - this.tooltipTargetRects.height / 2 <
                this.tooltipDimensions.height / 2;
        const invalidLeft = this.safeMargin + this.tooltipDimensions.width > this.tooltipTargetRects.left || invalidSide;
        const invalidRight =
            this.safeMargin + this.tooltipDimensions.width > getViewportWidth() - this.tooltipTargetRects.right || invalidSide;

        // responsive styleguide needs the tip on the right/left corner on the top or bottom of the tooltip
        if (this.positionTipOnCorner) {
            // if the tooltip target is positioned more than 50% from left, the tooltip will be positioned on the right
            if (horizontalMiddle > getViewportWidth() / 2) {
                this.tooltipTipAlignment = "right";
            } else {
                this.tooltipTipAlignment = "left";
            }

            if (this.align === "bottom" && !invalidBottom) {
                return this.setTooltipToBottomEdge(horizontalMiddle);
            }
            if (this.align === "top" && !invalidTop) {
                return this.setTooltipToTopEdge(horizontalMiddle);
            }

            // fallback if align is not set
            if (!invalidBottom) {
                return this.setTooltipToBottomEdge(horizontalMiddle);
            }
            if (!invalidTop) {
                return this.setTooltipToTopEdge(horizontalMiddle);
            }
            return this.setTooltipToBottomEdge(horizontalMiddle);
        }

        if (this.align === "top" && !invalidTop) {
            return this.setTooltipToTop(horizontalMiddle);
        }
        if (this.align === "bottom" && !invalidBottom) {
            return this.setTooltipToBottom(horizontalMiddle);
        }
        if (this.align === "left" && !invalidLeft) {
            return this.setTooltipToLeft(verticalMiddle);
        }
        if (this.align === "right" && !invalidRight) {
            return this.setTooltipToRight(verticalMiddle);
        }

        // try for valid Fallbacks
        if (!invalidTop) {
            return this.setTooltipToTop(horizontalMiddle);
        }
        if (!invalidBottom) {
            return this.setTooltipToBottom(horizontalMiddle);
        }
        if (!invalidRight) {
            return this.setTooltipToRight(verticalMiddle);
        }
        if (!invalidLeft) {
            return this.setTooltipToLeft(verticalMiddle);
        }
        // Fallback if there is no space anywhere
        return this.setTooltipToBottom(horizontalMiddle);
    }, 100);

    private deactivateTooltip = async () => {
        const deactivationTimeoutStart = new Date().getTime();
        setTimeout(() => {
            if (this.lastActivated < deactivationTimeoutStart) {
                this.isTooltipActive = false;
            }
        }, 700);
    };

    private setTooltipPosition = ({ top, left }: TooltipPosition) => {
        if (top) {
            this.element.style.setProperty("--ux-tooltip-box-top", `${top}px`);
        }
        if (left) {
            this.element.style.setProperty("--ux-tooltip-box-left", `${left}px`);
        }
    };

    /** setting the tooltip in different positions */
    private setTooltipToRight = (verticalMiddle: number) => {
        this.tooltipAlignment = "right";
        this.setTooltipPosition({
            top: verticalMiddle,
            left: this.tooltipTargetRects.right + this.safeMargin
        });
    };

    private setTooltipToLeft = (verticalMiddle: number) => {
        this.tooltipAlignment = "left";
        this.setTooltipPosition({
            top: verticalMiddle,
            left: this.tooltipTargetRects.left - this.safeMargin
        });
    };

    private setTooltipToTop = (horizontalMiddle: number) => {
        this.tooltipAlignment = "top";
        const willOverflowLeft = horizontalMiddle < this.tooltipDimensions.width / 2;
        const willOverflowRight = getViewportWidth() - horizontalMiddle < this.tooltipDimensions.width / 2;
        const leftPosition =
            !willOverflowLeft && !willOverflowRight
                ? horizontalMiddle
                : willOverflowLeft
                  ? 20 + this.tooltipDimensions.width / 2
                  : window.innerWidth - 30 - this.tooltipDimensions.width / 2;
        this.setTooltipPosition({
            top: this.tooltipTargetRects.top - this.safeMargin,
            left: leftPosition
        });
        this.calculateArrowPosition = true;
    };

    /**
     * bottom alignment will also cause an adjustment to the tooltip
     * pointer arrow position if necessary.
     */
    private setTooltipToBottom = (horizontalMiddle: number) => {
        this.tooltipAlignment = "bottom";
        const willOverflowLeft = horizontalMiddle < this.tooltipDimensions.width / 2;
        const willOverflowRight = getViewportWidth() - horizontalMiddle < this.tooltipDimensions.width / 2;
        const leftPosition =
            !willOverflowLeft && !willOverflowRight
                ? horizontalMiddle
                : willOverflowLeft
                  ? 20 + this.tooltipDimensions.width / 2
                  : window.innerWidth - 30 - this.tooltipDimensions.width / 2;
        this.setTooltipPosition({
            top: this.tooltipTargetRects.bottom + this.safeMargin,
            left: leftPosition
        });
        this.calculateTooltipMaxHeight = true;
        this.calculateArrowPosition = true;
    };

    private setTooltipToTopEdge = (horizontalMiddle: number) => {
        this.tooltipAlignment = "top-edge";
        this.setTooltipPosition({
            top: this.tooltipTargetRects.top - this.tooltipDimensions.height - this.safeMargin,
            left: horizontalMiddle
        });
        this.calculateArrowPosition = true;
    };

    private setTooltipToBottomEdge = (horizontalMiddle: number) => {
        this.tooltipAlignment = "bottom-edge";
        this.setTooltipPosition({
            top: this.tooltipTargetRects.bottom + this.safeMargin,
            left: horizontalMiddle
        });
        this.calculateArrowPosition = true;
    };

    componentDidRender() {
        if (!this.tooltipElement) {
            return;
        }

        if (this.calculateArrowPosition) {
            const tooltipRects = this.tooltipElement.getBoundingClientRect();
            const arrowPosition = this.tooltipTargetRects.left - tooltipRects.left + 18 + (this.tooltipTargetRects.width / 2 - 18);
            this.element.style.setProperty("--ux-tooltip-custom-tip-position", `${arrowPosition}px`);
            this.calculateArrowPosition = false;
        }
        if (this.calculateTooltipMaxHeight) {
            const tooltipRects = this.tooltipElement.getBoundingClientRect();
            const tooltipVerticalPadding = 60;
            const safeBottomDistance = 20;
            const tooltipMaxHeight = window.innerHeight - (tooltipRects.top + tooltipVerticalPadding + safeBottomDistance);
            // do not accept maxHeights less than 200: the user needs to scroll
            if (tooltipMaxHeight > 200 && this.tooltipAlignment === "bottom") {
                this.element.style.setProperty("--ux-tooltip-max-height", `${tooltipMaxHeight}px`);
                this.element.style.setProperty("--ux-tooltip-overflow-y", "auto");
            } else {
                this.element.style.removeProperty("--ux-tooltip-max-height");
                this.element.style.removeProperty("--ux-tooltip-overflow-y");
            }
        }
    }

    /** Preventing multiple Tooltip active at the same time */
    @Event({
        eventName: "uxTooltipActivated",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    activatedTooltipEvent!: EventEmitter<string>;

    @Listen("uxTooltipActivated", { target: "window" })
    preventMultipleTooltipsActive(event: CustomEvent) {
        if (event.detail !== this.uniqueId) {
            this.isTooltipActive = false;
        }
    }

    disconnectedCallback() {
        if (!this.tooltipTargetElement) {
            return;
        }
        this.tooltipTargetElement.removeEventListener("mouseenter", this.activateTooltip);
        this.tooltipElement?.removeEventListener("mouseenter", this.activateTooltip);
        this.tooltipTargetElement.removeEventListener("mouseleave", this.deactivateTooltip);
        this.tooltipElement?.removeEventListener("mouseleave", this.deactivateTooltip);
        this.tooltipElement?.removeEventListener("click", this.handleTooltipClick);
    }

    private addEventListeners = () => {
        this.removeEventListeners();
        if (this.tooltipTargetElement !== null) {
            this.tooltipTargetElement.addEventListener("mouseenter", this.activateTooltip);
            this.tooltipTargetElement.addEventListener("mouseleave", this.deactivateTooltip);
        }
        if (this.tooltipElement) {
            this.tooltipElement.addEventListener("mouseenter", this.activateTooltip);
            this.tooltipElement.addEventListener("mouseleave", this.deactivateTooltip);
            this.tooltipElement.addEventListener("click", this.handleTooltipClick);
        }
    };

    private removeEventListeners = () => {
        window.removeEventListener("scroll", this.scrollHandler);
        if (this.tooltipTargetElement !== null) {
            this.tooltipTargetElement.removeEventListener("mouseenter", this.activateTooltip);
            this.tooltipTargetElement.removeEventListener("mouseleave", this.deactivateTooltip);
        }
        if (this.tooltipElement) {
            this.tooltipElement.removeEventListener("mouseenter", this.activateTooltip);
            this.tooltipElement.removeEventListener("mouseleave", this.deactivateTooltip);
            this.tooltipElement.removeEventListener("click", this.handleTooltipClick);
        }
    };

    render() {
        const { tooltipAlignment, tooltipTipAlignment, textOnlyMode } = this;
        const Wrapper = textOnlyMode ? "p" : "div";

        return (
            <Host active={this.isTooltipActive}>
                <div
                    class={{
                        box: true,
                        "box--active": this.isTooltipActive && tooltipAlignment !== undefined,
                        [`box--${tooltipAlignment}`]: tooltipAlignment !== undefined,
                        [`box--tip-${tooltipTipAlignment}`]: tooltipTipAlignment !== undefined
                    }}
                    ref={(el) => (this.tooltipElement = el)}
                >
                    <div
                        class={{
                            tip: true,
                            [`tip--${this.tooltipTipAlignment}`]: this.tooltipTipAlignment !== undefined
                        }}
                    >
                        <div class="tip-shadow" />
                    </div>
                    <Wrapper
                        class={{
                            wrapper: true,
                            "wrapper--text-only-mode": textOnlyMode
                        }}
                    >
                        <slot />
                    </Wrapper>
                </div>
            </Host>
        );
    }
}

/** this variable is incremented by each instance */
let idCounter = 0;

interface TooltipPosition {
    top: number;
    left: number;
}
